/** Synchronous product lookups for Product ID cell validation, filled by async searches. */
import type { ProductVariant } from '@/types/feed/productVariant';

// null = searched and not found; absent = not searched yet, which validates fine.
const entries = new Map<string, ProductVariant | null>();
const listeners = new Set<() => void>();

/** Subscribes to lookup results; returns the unsubscribe. */
export function onProductRegistryChange(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Saves a search: each value gets the first variant with that id, else with that SKU, else null. */
export function rememberProducts(
  values: readonly string[],
  products: readonly ProductVariant[]
): void {
  for (const value of values) {
    const key = normalize(value);
    const product =
      products.find(product => String(product.id) === key) ??
      products.find(product => normalize(product.sku ?? '') === key);
    entries.set(key, product ?? null);
  }
  for (const listener of listeners) listener();
}

/** True when one of `products` has this id. */
export function hasId(products: readonly ProductVariant[], value: string): boolean {
  return products.some(product => String(product.id) === value.trim());
}

export function findProduct(id: string): ProductVariant | undefined {
  return entries.get(normalize(id)) ?? undefined;
}

export function isProductResolved(id: string): boolean {
  return entries.has(normalize(id));
}

export function isProductKnownInvalid(id: string): boolean {
  return entries.get(normalize(id)) === null;
}

function normalize(id: string): string {
  return id.trim().toLowerCase();
}
